import { useCallback, useEffect, useRef, useState } from 'react';
import { getJustPulled, type ApiClient, type JustPulledItem } from '../../modules/api';

const PER = 32;

export interface UseJustPulledPaginatedResult {
  items: JustPulledItem[];
  loading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  loadMore: () => void;
}

export interface UseJustPulledPaginatedOptions {
  /** Limit the feed to a single pack (pool_offer.id). Omit / null →
   *  global feed across all packs. */
  poolOfferId?: number | string | null;
}

/**
 * Append-only pagination for the full Just Pulled list view. Tracks the
 * accumulated items, the next page to fetch, and whether the backend
 * reported total_count <= what we already have (= reachedEnd).
 *
 * When `poolOfferId` is provided the feed is scoped to that single pack
 * — switching to a different scope resets the cursor and items.
 */
export function useJustPulledPaginated(
  client: ApiClient,
  options: UseJustPulledPaginatedOptions = {},
): UseJustPulledPaginatedResult {
  const { poolOfferId = null } = options;
  const [items, setItems] = useState<JustPulledItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const pageRef = useRef(1);
  const inFlightRef = useRef(false);
  const cancelledRef = useRef(false);

  const fetchPage = useCallback(
    async (page: number, isInitial: boolean) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (!isInitial) setLoadingMore(true);
      try {
        const res = await getJustPulled(client, {
          per: PER,
          page,
          ...(poolOfferId !== null && poolOfferId !== '' ? { poolOfferId } : {}),
        });
        if (cancelledRef.current) return;
        setItems((prev) => {
          const next = isInitial ? res.items : [...prev, ...res.items];
          if (res.totalCount > 0 && next.length >= res.totalCount) {
            setReachedEnd(true);
          } else if (res.items.length === 0) {
            setReachedEnd(true);
          }
          return next;
        });
        pageRef.current = page + 1;
      } catch {
        if (!cancelledRef.current) setReachedEnd(true);
      } finally {
        inFlightRef.current = false;
        if (!cancelledRef.current) {
          if (isInitial) setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [client, poolOfferId],
  );

  useEffect(() => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setItems([]);
    setLoading(true);
    setReachedEnd(false);
    void fetchPage(1, true);
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || reachedEnd) return;
    void fetchPage(pageRef.current, false);
  }, [loading, loadingMore, reachedEnd, fetchPage]);

  return { items, loading, loadingMore, reachedEnd, loadMore };
}
